import {
  GETApplicantSchoolYears,
  ProgramYear,
} from '@/apis/applicant_school_years/requests.ts'
import { Button } from '@/common/components/Button.tsx'
import LoadingCard from '@/common/components/LoadingCard.tsx'
import { ChildrenProp, RequestStatus } from '@/common/types_and_interfaces.ts'
import { FullCenteredMain } from '@/layouts/FullCenteredLayout.tsx'
import { useQuery } from '@tanstack/react-query'
import { createContext, useContext, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { UserQueries } from '@/queries/users/queries.ts'
import { useSignOut } from './common/hooks/useSignOut'
import { NoAccessPage } from './pages/authentication/NoAccessPage'

type SchoolYearContextProps = {
  applicantProgramYears: ProgramYear[]
  selectedProgramId: number
  setSelectedProgramId: (programId: number) => void
  hasAccess: boolean
}

export const SchoolYearContext = createContext<SchoolYearContextProps>({
  applicantProgramYears: [],
  selectedProgramId: 0,
  setSelectedProgramId: () => {},
  hasAccess: true,
})

export const SchoolYearProvider = ({ children }: ChildrenProp) => {
  const { t: translate } = useTranslation()
  const signOut = useSignOut()
  const [applicantProgramYears, setApplicantProgramYears] = useState<
    ProgramYear[]
  >([])
  const [selectedProgramId, setSelectedProgramId] = useState<number>(0)
  const [requestStatus, setRequestStatus] = useState<RequestStatus>('idle')

  const { data: permissionResponse, isLoading: isPermissionLoading } = useQuery(
    UserQueries.getApplicantPermissions(selectedProgramId)
  )

  const hasAccess = permissionResponse?.data?.has_access === true

  useEffect(() => {
    setRequestStatus('in_flight')
    GETApplicantSchoolYears() // this promise will never throw an error
      .then(async (response) => {
        if (response.error) {
          setRequestStatus('failed')
          console.log(response.error)
          return
        }

        let defaultProgram: ProgramYear | undefined
        defaultProgram = response.data.find(
          (program) => program.year_type == 'upcoming'
        )
        if (defaultProgram == undefined) {
          defaultProgram = response.data.find(
            (program) => program.year_type == 'current'
          )
        }
        if (defaultProgram == undefined) {
          defaultProgram = response.data.find(
            (program) => program.year_type == 'past'
          )
        }

        const programId = defaultProgram?.id ?? 0
        setSelectedProgramId(programId) // There will always be a default program, the endpoint errors when no programs are found.
        setApplicantProgramYears(response.data)

        setRequestStatus('success')
      })
  }, [])

  if (
    requestStatus === 'idle' ||
    requestStatus === 'in_flight' ||
    isPermissionLoading
  ) {
    return (
      <FullCenteredMain>
        <LoadingCard isLoading={true}>
          <span>{translate('retrieving_school_years')}</span>
        </LoadingCard>
      </FullCenteredMain>
    )
  }

  if (requestStatus === 'failed') {
    return (
      <>
        <FullCenteredMain>
          <LoadingCard>
            <span className="mb-2">
              {translate('unable_to_retrieve_settings')}
            </span>
            <p className="mb-14 text-base text-gray-500 font-normal">
              {translate('signed_out_title')}
            </p>
            <Button
              size="lg"
              onClick={() => {
                signOut()
              }}
            >
              {translate('back_to_login')}
            </Button>
          </LoadingCard>
        </FullCenteredMain>
      </>
    )
  }

  if (!hasAccess) {
    return <NoAccessPage />
  }

  return (
    <SchoolYearContext.Provider
      value={{
        applicantProgramYears,
        selectedProgramId,
        setSelectedProgramId,
        hasAccess,
      }}
    >
      {children}
    </SchoolYearContext.Provider>
  )
}

export const useApplicantProgramYears = () => {
  const context = useContext(SchoolYearContext)
  if (context === undefined) {
    throw new Error(
      'useSchoolYearContext must be used within a SchoolYearProvider'
    )
  }
  return context
}
